import { z } from "zod";

import { ModelPricingSchema, type ModelPricing } from "@quovy/usage";

import {
  AGENT_MODEL_REGISTRY,
  getAgentModelInfo,
  modelPricing,
  normalizeAgentModelId,
} from "./agent-models";
import {
  CLOUD_MODEL_GATEWAY_TYPES,
  DEFAULT_TIER_CHAINS,
  MODEL_TIERS,
  MODEL_TIER_LABELS,
  SUBSYSTEM_MODEL_DEFAULTS,
  gatewayTypeAllowsAmbientIdentity,
  resolveComputerUseToolKind,
  type ModelTier,
  type SubsystemModelDefaultKey,
} from "./model-providers";
import { providerCredentialRefSchema, providerCredentialScope } from "./provider-credentials";

// ---------------------------------------------------------------------------
// Workspace model policy: intelligence tiers, availability, and per-scope
// policy documents.
//
// A *tier* is a named quality level (fast / thinking / pro) that resolves to
// an ordered model fallback chain at call time. A *model reference* is the
// string persisted wherever a model id is stored today: either "tier:<name>"
// or a concrete catalog model id. A *policy document* is one JSON object per
// scope (platform, or one workspace) stored in the `model_policies` table;
// the workspace document merges over the platform document, and the merged
// result is evaluated against the deployment's routing config.
//
// This module is pure (no env/fs/db access) so the web bundle can import
// it. Loading documents from the database lives in `@quovy/model-policy`.
//
// Lifecycle invariant: persisted model ids — in rows and in documents — are
// never rewritten. Every id read from a document passes through
// `normalizeAgentModelId` (legacy-alias map), and ids that no longer resolve
// are skipped by resolution rather than failing it. Strict validation
// (`parseModelPolicyDocument`) applies to writes only.
//
// Exec plan: docs/exec-plans/active/workspace-model-policy.md
// ---------------------------------------------------------------------------

export const TIER_REFERENCE_PREFIX = "tier:";

export type ParsedModelReference =
  | { kind: "tier"; tier: ModelTier }
  | { kind: "model"; modelId: string };

/**
 * Syntactic parse of a persisted model reference. Concrete ids are
 * alias-normalized; existence checks are the caller's job (write paths are
 * strict, read paths tolerate drift). Returns null for malformed references
 * and for reserved/unknown tier names.
 */
export function parseModelReference(ref: string): ParsedModelReference | null {
  const trimmed = ref.trim();
  if (!trimmed) return null;
  if (trimmed.startsWith(TIER_REFERENCE_PREFIX)) {
    const tier = trimmed.slice(TIER_REFERENCE_PREFIX.length);
    if ((MODEL_TIERS as readonly string[]).includes(tier)) {
      return { kind: "tier", tier: tier as ModelTier };
    }
    return null;
  }
  return { kind: "model", modelId: normalizeAgentModelId(trimmed) };
}

export function isTierReference(ref: string): boolean {
  return parseModelReference(ref)?.kind === "tier";
}

export function formatTierReference(tier: ModelTier): string {
  return `${TIER_REFERENCE_PREFIX}${tier}`;
}

/** UI label for a reference: tier label, catalog label, or the raw id. */
export function describeModelReference(ref: string): string {
  const parsed = parseModelReference(ref);
  if (!parsed) return ref;
  if (parsed.kind === "tier") return MODEL_TIER_LABELS[parsed.tier];
  const entry = (AGENT_MODEL_REGISTRY.models as Record<string, { label: string } | undefined>)[
    parsed.modelId
  ];
  return entry?.label ?? parsed.modelId;
}

// --- Document schema ---------------------------------------------------------
// Structural validation only; strict semantic checks (model ids exist, scope
// rules) live in `parseModelPolicyDocument` so the read path can stay
// lenient (`modelPolicyDocumentSchema.safeParse`) and drift-tolerant.

const modelIdSchema = z.string().min(1);
const chainSchema = z.array(modelIdSchema).min(1);

const catalogExtensionEntrySchema = z.object({
  label: z.string().min(1),
  provider: z.string().min(1),
  inputCostPerMillionTokens: z.number().nonnegative(),
  outputCostPerMillionTokens: z.number().nonnegative(),
  cacheWriteCostPerMillionTokens: z.number().nonnegative().optional(),
  cacheReadCostPerMillionTokens: z.number().nonnegative().optional(),
  contextWindow: z.number().int().positive().optional(),
  supportsWebSearch: z.boolean().optional(),
  supportsComputerUse: z.boolean().optional(),
  // Lifecycle soft state, mirroring the registry flags: deprecated entries
  // keep resolving but new references are rejected on write; `sunsetAt`
  // (YYYY-MM-DD) is advisory only.
  deprecated: z.literal(true).optional(),
  sunsetAt: z.string().min(1).optional(),
});

export type CatalogExtensionEntry = z.infer<typeof catalogExtensionEntrySchema>;

// App-configured gateways mirror the layer-2 gateway union. Credentials are
// always opaque references: workspace documents use system-managed provider credentials; platform
// documents use customer-cloud secret stores or ambient workload identity.
// Secrets never appear in the document.
const workspaceGatewaySchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("anthropic-direct"), credential: providerCredentialRefSchema }),
  z.object({ type: z.literal("openai-direct"), credential: providerCredentialRefSchema }),
  z.object({ type: z.literal("google-direct"), credential: providerCredentialRefSchema }),
  z.object({ type: z.literal("xai-direct"), credential: providerCredentialRefSchema }),
  z.object({
    type: z.literal("bedrock"),
    region: z.string().min(1),
    credential: providerCredentialRefSchema,
  }),
  z
    .object({
      type: z.literal("azure-openai"),
      resourceName: z.string().min(1).optional(),
      baseURL: z.string().url().optional(),
      apiVersion: z.string().min(1).optional(),
      credential: providerCredentialRefSchema,
    })
    .superRefine((gateway, ctx) => {
      if (!gateway.resourceName && !gateway.baseURL) {
        ctx.addIssue({
          code: "custom",
          message: "azure-openai gateway requires resourceName or baseURL",
        });
      }
    }),
  z.object({
    type: z.literal("azure-foundry"),
    baseURL: z.string().url(),
    protocol: z.enum(["openai-compatible", "anthropic"]),
    credential: providerCredentialRefSchema,
  }),
  z.object({
    type: z.literal("openai-compatible"),
    baseURL: z.string().url(),
    credential: providerCredentialRefSchema,
  }),
]);

export type WorkspaceGatewayConfig = z.infer<typeof workspaceGatewaySchema>;

const workspaceRouteSchema = z
  .object({
    gateway: z.string().min(1),
    // Wire-format id (Bedrock inference profile, aggregator slug). Required
    // for cloud gateway types — enforced in parseModelPolicyDocument where the
    // gateway declaration is in reach.
    remoteModelId: z.string().min(1).optional(),
    // Provider-specific request options merged into the outbound call
    // (e.g. OpenRouter upstream-provider preferences).
    requestOverrides: z.record(z.string(), z.unknown()).optional(),
    // Replaces the catalog price card when this route serves the call.
    pricingOverrides: ModelPricingSchema.optional(),
    supportsWebSearch: z.boolean().optional(),
    computerUseTool: z.enum(["anthropic", "openai"]).nullable().optional(),
    // Keep the removed key visible to validation so stored policies receive a
    // migration instruction rather than losing the capability silently.
    supportsComputerUse: z.unknown().optional(),
  })
  .superRefine((route, ctx) => {
    if (Object.hasOwn(route, "supportsComputerUse")) {
      ctx.addIssue({
        code: "custom",
        path: ["supportsComputerUse"],
        message:
          "supportsComputerUse was replaced by computerUseTool. Set computerUseTool to openai or anthropic (or null to disable computer use), remove supportsComputerUse, then retry.",
      });
    }
  })
  .transform(({ supportsComputerUse: _removed, ...route }) => route);

export type WorkspaceRouteConfig = z.infer<typeof workspaceRouteSchema>;

export const modelPolicyDocumentSchema = z.object({
  // Platform scope only (enforced in parseModelPolicyDocument): catalog
  // extension entries merged over AGENT_MODEL_REGISTRY, enabling zero-deploy
  // model trials. Long-lived entries should be promoted into code.
  catalog: z.record(modelIdSchema, catalogExtensionEntrySchema).optional(),
  // Allowlist of catalog model ids. Wholesale-replace semantics: a workspace
  // document's availability fully replaces the platform's. Absent everywhere
  // means "all deployment-routed models".
  availability: z.array(modelIdSchema).optional(),
  tiers: z.partialRecord(z.enum(MODEL_TIERS), chainSchema).optional(),
  // Per-subsystem default reference (tier or concrete model). Honored from
  // M5 when subsystem defaults resolve through the policy layer.
  subsystems: z
    .partialRecord(
      z.enum(
        Object.keys(SUBSYSTEM_MODEL_DEFAULTS) as [
          SubsystemModelDefaultKey,
          ...SubsystemModelDefaultKey[],
        ],
      ),
      z.string().min(1),
    )
    .optional(),
  gateways: z.record(z.string().min(1), workspaceGatewaySchema).optional(),
  routes: z.record(modelIdSchema, workspaceRouteSchema).optional(),
});

export type ModelPolicyDocument = z.infer<typeof modelPolicyDocumentSchema>;

export type ModelPolicyScope = "platform" | "workspace";

export class ModelPolicyValidationError extends Error {
  readonly issues: string[];
  constructor(issues: string[]) {
    super(`Invalid model policy document: ${issues.join("; ")}`);
    this.name = "ModelPolicyValidationError";
    this.issues = issues;
  }
}

function knownModelIdsForDocument(
  document: ModelPolicyDocument,
  platformCatalog?: ModelPolicyDocument["catalog"],
): Set<string> {
  const known = new Set<string>(Object.keys(AGENT_MODEL_REGISTRY.models));
  for (const id of Object.keys(document.catalog ?? {})) known.add(normalizeAgentModelId(id));
  for (const id of Object.keys(platformCatalog ?? {})) known.add(normalizeAgentModelId(id));
  return known;
}

/**
 * A model reference found in a document, with where it came from. `kind`
 * distinguishes *selection* references (tier chains, subsystem defaults —
 * the ones deprecation gating applies to) from availability/route mentions.
 */
export type DocumentModelReference = {
  raw: string;
  modelId: string;
  path: string;
  kind: "availability" | "tier-chain" | "subsystem" | "route";
};

/** Every concrete model id a document mentions (normalized), with its path. */
export function collectDocumentModelReferences(
  document: ModelPolicyDocument,
): DocumentModelReference[] {
  const refs: DocumentModelReference[] = [];
  for (const raw of document.availability ?? []) {
    refs.push({
      raw,
      modelId: normalizeAgentModelId(raw),
      path: "availability",
      kind: "availability",
    });
  }
  for (const [tier, chain] of Object.entries(document.tiers ?? {})) {
    for (const raw of chain ?? []) {
      refs.push({
        raw,
        modelId: normalizeAgentModelId(raw),
        path: `tiers.${tier}`,
        kind: "tier-chain",
      });
    }
  }
  for (const [subsystem, ref] of Object.entries(document.subsystems ?? {})) {
    const parsed = ref ? parseModelReference(ref) : null;
    if (parsed?.kind === "model") {
      refs.push({
        raw: ref as string,
        modelId: parsed.modelId,
        path: `subsystems.${subsystem}`,
        kind: "subsystem",
      });
    }
  }
  for (const raw of Object.keys(document.routes ?? {})) {
    refs.push({ raw, modelId: normalizeAgentModelId(raw), path: "routes", kind: "route" });
  }
  return refs;
}

/**
 * Deprecated model ids visible to a document: registry flags plus extension
 * flags from the document's own catalog and (for workspace documents) the
 * platform catalog. Maps id → sunset date (null when none given).
 */
function deprecatedModelIdsForDocument(
  document: ModelPolicyDocument,
  platformCatalog?: ModelPolicyDocument["catalog"],
): Map<string, string | null> {
  const deprecated = new Map<string, string | null>();
  for (const [id, entry] of Object.entries(
    AGENT_MODEL_REGISTRY.models as Record<string, { deprecated?: true; sunsetAt?: string }>,
  )) {
    if (entry.deprecated) deprecated.set(id, entry.sunsetAt ?? null);
  }
  for (const catalog of [document.catalog, platformCatalog]) {
    for (const [id, entry] of Object.entries(catalog ?? {})) {
      if (entry?.deprecated) deprecated.set(normalizeAgentModelId(id), entry.sunsetAt ?? null);
    }
  }
  return deprecated;
}

/**
 * Strict parse for write paths. Beyond the structural schema: enforces the
 * scope rule (catalog extensions are platform-only), checks every model id
 * (after alias normalization) against the catalog — base registry plus the
 * document's own extensions plus `platformCatalog` (pass the platform
 * document's catalog when validating a workspace document) — and requires
 * `remoteModelId` on routes through document-declared cloud gateways.
 *
 * Deprecation gating: *selection* references (tier-chain entries, subsystem
 * defaults) to deprecated models are rejected unless the same model was
 * already selected in `previousDocument` — deprecating a model blocks new
 * pins without breaking a document that legitimately still carries it.
 * Availability entries and routes are exempt: keeping a deprecated model
 * available (existing agents still run it) or re-routing its transport must
 * stay possible.
 *
 * Read paths must NOT use this: they safeParse the structural schema and
 * tolerate drift.
 */
export function parseModelPolicyDocument(
  input: unknown,
  scope: ModelPolicyScope,
  opts?: {
    platformCatalog?: ModelPolicyDocument["catalog"];
    previousDocument?: ModelPolicyDocument | null;
  },
): ModelPolicyDocument {
  const structural = modelPolicyDocumentSchema.safeParse(input);
  if (!structural.success) {
    throw new ModelPolicyValidationError(
      structural.error.issues.map(
        (issue) => `${issue.path.join(".") || "document"}: ${issue.message}`,
      ),
    );
  }
  const document = structural.data;
  const issues: string[] = [];

  if (scope === "workspace" && document.catalog) {
    issues.push("catalog: extension entries are only valid in the platform document");
  }

  for (const [name, gateway] of Object.entries(document.gateways ?? {})) {
    const credentialScope = providerCredentialScope(gateway.credential);
    if (scope === "platform" && credentialScope === "workspace") {
      issues.push(
        `gateways.${name}.credential: platform gateways must use a customer-cloud secret reference or ambient workload identity; workspace provider credentials belong to one workspace`,
      );
    }
    if (scope === "workspace" && credentialScope === "deployment") {
      issues.push(
        `gateways.${name}.credential: workspace gateways must use a workspace provider credential; deployment secret references and ambient identity can affect other workspaces`,
      );
    }
    const keyless = gateway.credential.source === "none";
    const ambient = gateway.credential.source === "ambient";
    if (keyless && gateway.type !== "openai-compatible") {
      issues.push(
        `gateways.${name}.credential: only openai-compatible gateways may use a keyless connection`,
      );
    }
    if (ambient && !gatewayTypeAllowsAmbientIdentity(gateway.type)) {
      issues.push(
        `gateways.${name}.credential: only Bedrock, Azure OpenAI, and Azure AI Foundry gateways may use ambient workload identity`,
      );
    }
  }

  const known = knownModelIdsForDocument(document, opts?.platformCatalog);
  const requireKnown = (id: string, path: string) => {
    if (!known.has(normalizeAgentModelId(id))) {
      issues.push(`${path}: unknown model id "${id}"`);
    }
  };

  for (const id of document.availability ?? []) requireKnown(id, "availability");
  for (const [tier, chain] of Object.entries(document.tiers ?? {})) {
    for (const id of chain ?? []) requireKnown(id, `tiers.${tier}`);
  }
  for (const [subsystem, ref] of Object.entries(document.subsystems ?? {})) {
    const parsed = ref ? parseModelReference(ref) : null;
    if (!parsed) {
      issues.push(`subsystems.${subsystem}: invalid model reference "${ref}"`);
    } else if (parsed.kind === "model") {
      requireKnown(parsed.modelId, `subsystems.${subsystem}`);
    }
  }
  for (const id of Object.keys(document.routes ?? {})) requireKnown(id, "routes");
  for (const [modelId, route] of Object.entries(document.routes ?? {})) {
    if (!route) continue;
    const gateway = document.gateways?.[route.gateway];
    // Routes may also name a deployment gateway; those are validated where
    // the deployment config is in reach (the API layer).
    if (
      gateway &&
      (CLOUD_MODEL_GATEWAY_TYPES as readonly string[]).includes(gateway.type) &&
      !route.remoteModelId
    ) {
      issues.push(
        `routes.${modelId}: routes through "${gateway.type}" gateways require remoteModelId`,
      );
    }
    if (gateway && route.computerUseTool) {
      const catalogContext: ModelPolicyDocument = {
        ...document,
        ...(scope === "workspace" && opts?.platformCatalog
          ? { catalog: opts.platformCatalog }
          : {}),
      };
      const entry = effectiveCatalogEntry(catalogContext, modelId);
      const toolKind = entry
        ? resolveComputerUseToolKind({
            catalogProvider: entry.provider,
            catalogSupportsComputerUse: entry.supportsComputerUse,
            gatewayType: gateway.type,
            gatewayProtocol: gateway.type === "azure-foundry" ? gateway.protocol : undefined,
            configuredTool: route.computerUseTool,
          })
        : null;
      if (!toolKind) {
        issues.push(
          `routes.${modelId}.computerUseTool: route cannot use the ` +
            `"${route.computerUseTool}" computer tool through ${gateway.type} for catalog ` +
            `provider "${entry?.provider ?? "unknown"}". Choose openai for OpenAI/Azure OpenAI, ` +
            "anthropic for Anthropic/Anthropic-on-Bedrock or Foundry, or null to disable computer use, then retry.",
        );
      }
    }
  }

  const deprecated = deprecatedModelIdsForDocument(document, opts?.platformCatalog);
  if (deprecated.size > 0) {
    const isSelection = (ref: DocumentModelReference) =>
      ref.kind === "tier-chain" || ref.kind === "subsystem";
    const previouslySelected = new Set(
      opts?.previousDocument
        ? collectDocumentModelReferences(opts.previousDocument)
            .filter(isSelection)
            .map((ref) => ref.modelId)
        : [],
    );
    for (const ref of collectDocumentModelReferences(document).filter(isSelection)) {
      if (deprecated.has(ref.modelId) && !previouslySelected.has(ref.modelId)) {
        issues.push(
          `${ref.path}: "${ref.raw}" is deprecated — new references are not allowed ` +
            `(existing references keep resolving)`,
        );
      }
    }
  }

  if (issues.length > 0) throw new ModelPolicyValidationError(issues);
  return document;
}

// --- Merge + resolution ------------------------------------------------------

/**
 * Merge a workspace document over the platform document. Availability
 * replaces wholesale; tiers, subsystems, gateways, and routes merge per-key
 * with workspace winning; catalog extensions come from the platform document
 * only. Null/absent documents merge as empty.
 */
export function mergeModelPolicies(
  platform: ModelPolicyDocument | null,
  workspace: ModelPolicyDocument | null,
): ModelPolicyDocument {
  const p = platform ?? {};
  const w = workspace ?? {};
  const merged: ModelPolicyDocument = {};
  if (p.catalog) merged.catalog = p.catalog;
  const availability = w.availability ?? p.availability;
  if (availability) merged.availability = availability;
  const tiers = { ...p.tiers, ...w.tiers };
  if (Object.keys(tiers).length > 0) merged.tiers = tiers;
  const subsystems = { ...p.subsystems, ...w.subsystems };
  if (Object.keys(subsystems).length > 0) merged.subsystems = subsystems;
  const gateways = { ...p.gateways, ...w.gateways };
  if (Object.keys(gateways).length > 0) merged.gateways = gateways;
  const routes = { ...p.routes, ...w.routes };
  if (Object.keys(routes).length > 0) merged.routes = routes;
  return merged;
}

/** The effective chain for a tier: merged override, else the code default. */
export function effectiveTierChain(merged: ModelPolicyDocument, tier: ModelTier): string[] {
  const chain = merged.tiers?.[tier] ?? DEFAULT_TIER_CHAINS[tier];
  return chain.map((id) => normalizeAgentModelId(id));
}

export type PolicyDecision = {
  modelId: string;
  via: "reference" | "chain";
};

/**
 * Resolve a model reference against a merged policy document and the set of
 * model ids the deployment can route. Pure. Returns null when nothing in
 * the reference survives the filters. Callers decide what that means:
 * concrete pins fail to preserve model identity, while explicit tiers may
 * continue to a separately declared subsystem default.
 *
 * Filters, in order: known to the catalog (base registry ∪ platform
 * extensions), allowed by effective availability (absent = all), and
 * routable by the deployment. Unknown/stale entries are skipped silently —
 * the lifecycle invariant means documents are never rewritten to remove
 * them.
 */
export function resolvePolicyDecision(input: {
  routedModelIds: ReadonlySet<string>;
  merged: ModelPolicyDocument;
  ref: string;
}): PolicyDecision | null {
  const { routedModelIds, merged, ref } = input;
  const parsed = parseModelReference(ref);
  if (!parsed) return null;

  const known = knownModelIdsForDocument(merged);
  const availability = merged.availability
    ? new Set(merged.availability.map((id) => normalizeAgentModelId(id)))
    : null;
  const allowed = (id: string) =>
    known.has(id) && (availability === null || availability.has(id)) && routedModelIds.has(id);

  if (parsed.kind === "model") {
    return allowed(parsed.modelId) ? { modelId: parsed.modelId, via: "reference" } : null;
  }
  for (const id of effectiveTierChain(merged, parsed.tier)) {
    if (allowed(id)) return { modelId: id, via: "chain" };
  }
  return null;
}

/**
 * The model ids a workspace's pickers should offer: effective availability
 * intersected with what the deployment routes.
 */
export function effectiveAvailableModelIds(
  merged: ModelPolicyDocument,
  routedModelIds: ReadonlySet<string>,
): string[] {
  const known = knownModelIdsForDocument(merged);
  const availability = merged.availability
    ? new Set(merged.availability.map((id) => normalizeAgentModelId(id)))
    : null;
  return [...routedModelIds].filter(
    (id) => known.has(id) && (availability === null || availability.has(id)),
  );
}

// --- Effective catalog + lint ------------------------------------------------

/**
 * One catalog view over the base registry and the platform document's
 * extension entries — label, price card, capabilities, and lifecycle flags
 * for any model id the policy system can reference. The base registry is
 * never mutated; extensions layer over it at read time.
 */
export type EffectiveCatalogEntry = {
  modelId: string;
  label: string;
  provider: string;
  pricing: ModelPricing;
  supportsWebSearch: boolean;
  supportsComputerUse: boolean;
  deprecated: boolean;
  sunsetAt: string | null;
  source: "registry" | "extension";
};

/** Registry entry, else the merged document's extension entry, else null. */
export function effectiveCatalogEntry(
  merged: ModelPolicyDocument,
  modelId: string,
): EffectiveCatalogEntry | null {
  const normalized = normalizeAgentModelId(modelId);
  const info = getAgentModelInfo(normalized);
  if (info) {
    return {
      modelId: normalized,
      label: info.label,
      provider: info.provider,
      pricing: modelPricing(normalized) ?? {
        inputCostPerMillionTokens: 0,
        outputCostPerMillionTokens: 0,
      },
      supportsWebSearch: info.supportsWebSearch === true,
      supportsComputerUse: info.supportsComputerUse === true,
      deprecated: info.deprecated === true,
      sunsetAt: info.sunsetAt ?? null,
      source: "registry",
    };
  }
  for (const [id, entry] of Object.entries(merged.catalog ?? {})) {
    if (!entry || normalizeAgentModelId(id) !== normalized) continue;
    return {
      modelId: normalized,
      label: entry.label,
      provider: entry.provider,
      pricing: {
        inputCostPerMillionTokens: entry.inputCostPerMillionTokens,
        outputCostPerMillionTokens: entry.outputCostPerMillionTokens,
        cacheWriteCostPerMillionTokens: entry.cacheWriteCostPerMillionTokens,
        cacheReadCostPerMillionTokens: entry.cacheReadCostPerMillionTokens,
      },
      supportsWebSearch: entry.supportsWebSearch === true,
      supportsComputerUse: entry.supportsComputerUse === true,
      deprecated: entry.deprecated === true,
      sunsetAt: entry.sunsetAt ?? null,
      source: "extension",
    };
  }
  return null;
}

/**
 * Read-only advice about a merged policy document — nothing here blocks a
 * write or rewrites a document (the lifecycle invariant). `servableModelIds`
 * is deployment routes plus document routes; `gatewayNames` is the
 * deployment's gateway names (document gateways are read off `merged`).
 *
 * Configs are shared across deployments, so "no gateway route" warnings can
 * be legitimate — a chain entry another deployment routes is fine there.
 */
export function lintModelPolicyDocument(input: {
  merged: ModelPolicyDocument;
  scope: ModelPolicyScope;
  servableModelIds: ReadonlySet<string>;
  gatewayNames: ReadonlySet<string>;
}): string[] {
  const { merged, scope, servableModelIds, gatewayNames } = input;
  const warnings: string[] = [];
  const known = knownModelIdsForDocument(merged);
  const deprecated = deprecatedModelIdsForDocument(merged);

  for (const tier of MODEL_TIERS) {
    const chain = merged.tiers?.[tier] ?? DEFAULT_TIER_CHAINS[tier];
    for (const raw of chain) {
      const id = normalizeAgentModelId(raw);
      if (!known.has(id)) {
        warnings.push(`tiers.${tier}: unknown model "${raw}" — entry is skipped by resolution`);
      } else if (!servableModelIds.has(id)) {
        warnings.push(`tiers.${tier}: "${id}" has no gateway route in this deployment`);
      }
    }
    if (!chain.some((raw) => servableModelIds.has(normalizeAgentModelId(raw)))) {
      warnings.push(`tiers.${tier}: no chain entry is routable in this deployment`);
    }
  }

  for (const raw of merged.availability ?? []) {
    const id = normalizeAgentModelId(raw);
    if (!known.has(id)) {
      warnings.push(`availability: unknown model "${raw}" — entry is skipped by resolution`);
    } else if (!servableModelIds.has(id)) {
      warnings.push(`availability: "${id}" has no gateway route in this deployment`);
    }
  }

  for (const [subsystem, ref] of Object.entries(merged.subsystems ?? {})) {
    const parsed = ref ? parseModelReference(ref) : null;
    if (parsed?.kind === "model" && !known.has(parsed.modelId)) {
      warnings.push(
        `subsystems.${subsystem}: unknown model "${ref}" — calls fail until this exact model is configured or another model is selected`,
      );
    }
  }

  for (const [rawId, route] of Object.entries(merged.routes ?? {})) {
    if (!route) continue;
    const id = normalizeAgentModelId(rawId);
    if (!known.has(id)) {
      warnings.push(`routes: unknown model "${rawId}" — the route serves nothing`);
    }
    if (!merged.gateways?.[route.gateway] && !gatewayNames.has(route.gateway)) {
      warnings.push(
        `routes.${id}: gateway "${route.gateway}" is not defined in this document or deployment`,
      );
    }
    if (
      route.requestOverrides &&
      !merged.gateways?.[route.gateway] &&
      gatewayNames.has(route.gateway)
    ) {
      warnings.push(
        `routes.${id}: requestOverrides require a document-declared gateway — this pin to ` +
          `deployment gateway "${route.gateway}" fails at call time`,
      );
    }
  }

  for (const ref of collectDocumentModelReferences(merged)) {
    if (ref.raw !== ref.modelId && !ref.raw.includes("/")) {
      warnings.push(
        `${ref.path}: "${ref.raw}" is an alias of "${ref.modelId}" — calls already resolve via the alias; update at leisure`,
      );
    }
    const sunsetAt = deprecated.get(ref.modelId);
    if (deprecated.has(ref.modelId)) {
      warnings.push(
        `${ref.path}: "${ref.raw}" references deprecated model "${ref.modelId}"` +
          (sunsetAt ? ` (sunset ${sunsetAt})` : ""),
      );
    }
  }

  if (scope === "platform") {
    for (const id of Object.keys(merged.catalog ?? {})) {
      warnings.push(`catalog: "${id}" is an extension entry — promote to code when stable`);
    }
  }

  return warnings;
}
